<script setup lang="ts">
import { mdiCheckBold, mdiPlay, mdiStop } from '@mdi/js';
import { computed } from 'vue';
import type { ZeroMeasurementButtonPhase } from './utils/zeroMeasurementStartButton';

/**
 * What sits on the pie of the zero measurement start button. Measuring, the
 * percentage, with the stop icon behind it for the button's hover and focus.
 * Checked (the run just succeeded, or the latest zero measurement is valid),
 * a bold green check, with the play icon behind it likewise. Otherwise the
 * play icon alone. The content swaps by a cross-fade, except that the check
 * enters with a pop after a successful run and holds for a moment; the end of
 * that animation is reported as `settled`, the host's cue to drop back to
 * idle. The check then stays while the validity lasts: the idle hub is the
 * same keyed element, so the end of the phase swaps nothing.
 */
const props = defineProps<{
  phase: ZeroMeasurementButtonPhase;
  /** Show the check; read while not measuring. */
  checked?: boolean;
  /** The percentage shown while measuring. */
  progressText: string;
}>();

const emits = defineEmits<{
  /** Succeeded: the check has finished its pop and its hold. */
  settled: [];
}>();

const ICON_SIZE = 20;
const measuring = computed(() => props.phase === 'measuring');
const succeeded = computed(() => props.phase === 'succeeded');
const transition = computed(() => (succeeded.value ? 'hub-pop' : 'hub'));
</script>

<template>
  <Transition :name="transition" mode="out-in">
    <span v-if="measuring" key="progress" class="hub">
      <span class="hub__progress" data-testid="ZeroMeasurementProgress">
        {{ progressText }}
      </span>
      <v-icon
        class="hub__stop"
        :icon="mdiStop"
        :size="ICON_SIZE"
        data-testid="ZeroMeasurementStopIcon"
      />
    </span>
    <span v-else-if="checked" key="valid" class="hub" :class="{ 'hub--succeeded': succeeded }">
      <v-icon
        class="hub__icon hub__check"
        :icon="mdiCheckBold"
        :size="ICON_SIZE"
        data-testid="ZeroMeasurementCheckIcon"
        @animationend="emits('settled')"
      />
      <v-icon
        class="hub__icon hub__play"
        :icon="mdiPlay"
        :size="ICON_SIZE"
        data-testid="ZeroMeasurementPlayIcon"
      />
    </span>
    <v-icon
      v-else
      key="play"
      class="hub__icon"
      :icon="mdiPlay"
      :size="ICON_SIZE"
      data-testid="ZeroMeasurementPlayIcon"
    />
  </Transition>
</template>

<style lang="scss" scoped>
$hub-fade: 0.3s;
$check-pop: 0.6s;
$check-hold: 0.4s;
$progress-font-size: 12px;

// The progress and the stop icon, or the check and the play icon, stacked
// on the center; the button's hover or focus cross-fades from one to the
// other (see its styles).
.hub {
  position: absolute;
  inset: 0;
  pointer-events: none;

  // Progress in the hub: bold, small enough for "100%" to clear the pie's
  // edge, tabular so it stays centered as the digits change.
  &__progress,
  &__stop {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    transition: opacity $hub-fade ease;
  }

  &__progress {
    font-size: $progress-font-size;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  &__stop {
    opacity: 0;
  }

  // Centered on the pie, above the wedge.
  &__icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  &__check,
  &__play {
    transition: opacity $hub-fade ease;
  }

  &__check {
    color: rgb(var(--v-theme-success));
  }

  &__play {
    opacity: 0;
  }

  // After a successful run the check pops in and holds; the hold is part of
  // the keyframes so that one animationend marks the end of the whole phase.
  &--succeeded &__check {
    animation: check-pop ($check-pop + $check-hold) ease-out both;
  }
}

// One quick pop: up past full size, a dip, then at rest for the hold.
@keyframes check-pop {
  0% {
    transform: translate(-50%, -50%) scale(0);
  }
  35% {
    transform: translate(-50%, -50%) scale(1.3);
  }
  50% {
    transform: translate(-50%, -50%) scale(0.9);
  }
  60%,
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
}

// A still check for the same span: the outcome stays readable, and the
// animation still ends.
@keyframes check-hold {
  from,
  to {
    transform: translate(-50%, -50%);
  }
}

// Hub swap: the old content fades out completely, then the new one fades in.
// The check instead pops in (its own animation) once the old content is gone.
.hub-enter-active,
.hub-leave-active,
.hub-pop-leave-active {
  transition: opacity $hub-fade ease;
}

.hub-enter-from,
.hub-leave-to,
.hub-pop-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .hub__progress,
  .hub__stop,
  .hub__check,
  .hub__play,
  .hub-enter-active,
  .hub-leave-active,
  .hub-pop-leave-active {
    transition: none;
  }

  .hub--succeeded .hub__check {
    animation-name: check-hold;
  }
}
</style>
